import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { calendarParse, calendarImport, createVoyage } from "@/lib/voyagesApi";
import { getSettings } from "@/lib/settingsApi";
import { todayISO, addDays, generateDefaultDays, formatDate } from "@/lib/dateHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import DaysTable from "@/components/DaysTable";
import { toast } from "@/hooks/use-toast";
import {
  Upload, FileSpreadsheet, PenLine, ArrowLeft, ArrowRight,
  Loader2, Check, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

const STEPS = ["Method", "Source Data", "Review & Create"];

export default function VoyageNew() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = parseInt(searchParams.get("step") || "1", 10);
  const setStep = (s) => {
    const p = new URLSearchParams(searchParams);
    p.set("step", String(s));
    setSearchParams(p, { replace: true });
  };

  // ── Step 1: Method ──────────────────────────────────────────
  const [method, setMethod] = useState(searchParams.get("method") || "");

  // ── Step 2 upload state ─────────────────────────────────────
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [headerRow, setHeaderRow] = useState(0);
  const [dateCol, setDateCol] = useState(null);
  const [portCol, setPortCol] = useState(null);
  const [departureCol, setDepartureCol] = useState(null);
  const [arrivalCol, setArrivalCol] = useState(null);
  const [seaPortCol, setSeaPortCol] = useState(null);
  // Phase 7+ — user-picked voyage window (used to filter the imported
  // calendar down to just this voyage's days). Pre-filled from the parse
  // response's suggested_voyage; user can adjust before clicking Import.
  const [uploadStartDate, setUploadStartDate] = useState("");
  const [uploadTotalDays, setUploadTotalDays] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ── Step 2 manual state ─────────────────────────────────────
  const [manualStartDate, setManualStartDate] = useState(todayISO());
  const [manualTotalDays, setManualTotalDays] = useState(7);

  // ── Step 3: Voyage header + days ────────────────────────────
  const [voyageNumber, setVoyageNumber] = useState("");
  const [shipName, setShipName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [totalDays, setTotalDays] = useState(0);
  const [guestCount, setGuestCount] = useState(0);
  const [kidsCount, setKidsCount] = useState(0);
  const [days, setDays] = useState([]);
  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Pre-fill ship name from app settings (default_ship_name singleton).
  useEffect(() => {
    getSettings()
      .then((s) => setShipName((curr) => curr || s.default_ship_name || ""))
      .catch(() => {});
  }, []);

  // ── Handlers ────────────────────────────────────────────────
  const handleMethodSelect = (m) => {
    setMethod(m);
    const p = new URLSearchParams(searchParams);
    p.set("step", "2");
    p.set("method", m);
    setSearchParams(p, { replace: true });
  };

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParsing(true);
    setParseResult(null);
    setImportResult(null);
    try {
      const result = await calendarParse(f);
      setParseResult(result);
      if (result.sheet_names?.length) setSelectedSheet(result.sheet_names[0]);
      const dc = result.detected_columns || {};
      setHeaderRow(dc.header_row ?? 0);
      setDateCol(dc.date_col);
      setPortCol(dc.port_col);
      setDepartureCol(dc.departure_col);
      setArrivalCol(dc.arrival_col);
      setSeaPortCol(dc.sea_port_col);
      // Phase 7+ — seed the windowing inputs from the suggested voyage.
      // User can override before clicking Import.
      const sv = result.suggested_voyage || {};
      setUploadStartDate(sv.start_date || "");
      setUploadTotalDays(sv.total_days || 0);
    } catch (err) {
      toast({ title: "Parse failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!file || dateCol == null) return;
    setImporting(true);
    try {
      const result = await calendarImport(file, {
        sheet_name: selectedSheet,
        header_row: headerRow,
        date_col: dateCol,
        port_col: portCol,
        departure_col: departureCol,
        arrival_col: arrivalCol,
        sea_port_col: seaPortCol,
        // Phase 7+ — windowing. Send only when BOTH are set; backend
        // falls back to full-file import when either is missing.
        ...(uploadStartDate && uploadTotalDays > 0
          ? { start_date: uploadStartDate, total_days: uploadTotalDays }
          : {}),
      });
      setImportResult(result);
      // Pre-fill step 3 from import result
      const sv = result.suggested_voyage || {};
      setDays(result.days || []);
      setTotalDays(result.days?.length || sv.total_days || 7);
      setStartDate(sv.start_date || result.days?.[0]?.date || todayISO());
      setVoyageNumber(sv.voyage_number || "");
      setShipName(sv.ship_name || "");
      toast({ title: "Import successful", description: `${result.days?.length || 0} days imported.` });
    } catch (err) {
      toast({ title: "Import failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const prepareManualStep3 = () => {
    const d = generateDefaultDays(manualStartDate, manualTotalDays);
    setDays(d);
    setStartDate(manualStartDate);
    setTotalDays(manualTotalDays);
  };

  const handleToStep3 = () => {
    if (method === "manual") prepareManualStep3();
    setStep(3);
  };

  const canProceedStep2 = method === "manual"
    ? (manualStartDate && manualTotalDays >= 1)
    : (method === "upload" && importResult && importResult.days?.length > 0);

  const handleCreate = async () => {
    setCreating(true);
    setSubmitError(null);
    try {
      const payload = {
        voyage_number: voyageNumber,
        ship_id: null,
        ship_name: shipName,
        start_date: startDate,
        total_days: totalDays,
        guest_count: guestCount,
        kids_count: kidsCount,
        days,
      };
      const result = await createVoyage(payload);
      toast({ title: "Voyage created", description: `${result.voyage_number} created successfully.` });
      navigate(`/voyages/${result.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Create failed";
      setSubmitError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setCreating(false);
    }
  };

  // ── Column option labels ────────────────────────────────────
  const getColOptions = () => {
    if (!parseResult?.preview?.length) return [];
    const headerRowData = parseResult.preview[headerRow] || [];
    return headerRowData.map((cell, idx) => ({
      value: String(idx),
      label: `Col ${String.fromCharCode(65 + idx)}${cell ? ` (${String(cell).slice(0, 20)})` : ""}`,
    }));
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="px-6 py-8 max-w-4xl" data-testid="voyage-new-page">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/voyages")} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-bold tracking-tight">New Voyage</h1>
      </div>

      {/* Stepper */}
      <div className="mb-8 flex items-center gap-2" data-testid="wizard-stepper">
        {STEPS.map((label, idx) => {
          const s = idx + 1;
          const active = s === step;
          const done = s < step;
          return (
            <div key={s} className="flex items-center gap-2">
              {idx > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <div className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                active ? "bg-primary text-primary-foreground" : done ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              )}>
                {done ? <Check className="h-3 w-3" /> : <span>{s}</span>}
                <span className="hidden sm:inline">{label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Step 1: Method ─────────────────────────────────── */}
      {step === 1 && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="wizard-step-1">
          <button
            onClick={() => handleMethodSelect("upload")}
            data-testid="wizard-method-upload"
            className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <FileSpreadsheet className="h-10 w-10 text-primary" />
            <span className="text-sm font-semibold">Upload cruise calendar (.xlsx)</span>
            <span className="text-xs text-muted-foreground text-center">Parse an Excel calendar to auto-detect dates, ports, and departures.</span>
          </button>
          <button
            onClick={() => handleMethodSelect("manual")}
            data-testid="wizard-method-manual"
            className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PenLine className="h-10 w-10 text-primary" />
            <span className="text-sm font-semibold">Manual entry</span>
            <span className="text-xs text-muted-foreground text-center">Set start date and number of days, then customize the itinerary.</span>
          </button>
        </div>
      )}

      {/* ── Step 2: Upload ─────────────────────────────────── */}
      {step === 2 && method === "upload" && (
        <div className="space-y-6" data-testid="wizard-step-2-upload">
          {/* File picker */}
          <div>
            <Label>Calendar File</Label>
            <input
              ref={fileRef} type="file" accept=".xlsx"
              onChange={handleFilePick} className="hidden"
              data-testid="wizard-file-input"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={parsing}>
                {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {file ? file.name : "Choose .xlsx file"}
              </Button>
            </div>
          </div>

          {/* Parse results */}
          {parseResult && (
            <>
              {/* Sheet selector */}
              <div className="space-y-1.5">
                <Label>Sheet</Label>
                <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                  <SelectTrigger className="w-[250px]" data-testid="wizard-sheet-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {parseResult.sheet_names?.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview grid */}
              {parseResult.preview && (
                <div className="space-y-1.5">
                  <Label>Preview (first 30 rows)</Label>
                  <div className="overflow-x-auto rounded-lg border max-h-[300px] overflow-y-auto">
                    <table className="w-full text-xs font-mono">
                      <thead className="sticky top-0 bg-muted">
                        <tr>
                          <th className="px-2 py-1 text-left text-muted-foreground border-r w-10">#</th>
                          {Array.from({ length: Math.min(8, parseResult.preview[0]?.length || 0) }).map((_, ci) => (
                            // Excel preview columns are POSITIONAL (column letter A, B, C…
                            // IS the identity), so the column index is the
                            // canonical stable key here, not a value-derived hash.
                            // We prefix to make the intent self-evident in DevTools.
                            <th key={`xlsx-col-${ci}`} className="px-2 py-1 text-left min-w-[100px] border-r">
                              {String.fromCharCode(65 + ci)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parseResult.preview.slice(0, 30).map((row, ri) => (
                          // Excel rows are positional snapshot data — no
                          // semantic id exists. Index is canonical here.
                          <tr key={`xlsx-row-${ri}`} className={cn(
                            ri === headerRow ? "bg-primary/10 font-semibold" : ri % 2 === 0 ? "" : "bg-muted/30"
                          )}>
                            <td className="px-2 py-1 text-muted-foreground border-r">{ri}</td>
                            {Array.from({ length: Math.min(8, row?.length || 0) }).map((_, ci) => (
                              <td key={`xlsx-row-${ri}-col-${ci}`} className="px-2 py-1 border-r truncate max-w-[150px]">
                                {row[ci] == null || row[ci] === "" ? "—" : String(row[ci])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Column mapping */}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1">
                  <Label>Header Row</Label>
                  <Input type="number" min={0} value={headerRow} onChange={(e) => setHeaderRow(parseInt(e.target.value, 10) || 0)} data-testid="wizard-header-row" />
                </div>
                {[
                  { key: "date", val: dateCol, set: setDateCol, label: "Date Column", tid: "wizard-date-col", required: true },
                  { key: "port", val: portCol, set: setPortCol, label: "Port Column", tid: "wizard-port-col" },
                  { key: "arrival", val: arrivalCol, set: setArrivalCol, label: "Arrival Column", tid: "wizard-arrival-col" },
                  { key: "departure", val: departureCol, set: setDepartureCol, label: "Departure Column", tid: "wizard-departure-col" },
                  { key: "seaport", val: seaPortCol, set: setSeaPortCol, label: "Sea/Port Column", tid: "wizard-seaport-col" },
                ].map(({ val, set, label, tid }) => (
                  <div key={tid} className="space-y-1">
                    <Label>{label}</Label>
                    <Select
                      value={val != null ? String(val) : "none"}
                      onValueChange={(v) => set(v === "none" ? null : parseInt(v, 10))}
                    >
                      <SelectTrigger data-testid={tid}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {getColOptions().map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Phase 7+ — Voyage window. When a calendar contains many
                  more days than this voyage (year-long file etc.), we
                  filter the import down to just the days inside
                  [start_date, start_date + total_days - 1]. Both fields
                  must be set for the filter to engage; leaving either
                  blank imports everything (legacy behaviour). */}
              <div className="rounded-md border border-sky-200 bg-sky-50/50 p-3">
                <Label className="text-xs font-semibold uppercase tracking-wider text-sky-900">
                  Voyage Window (optional — only imports days inside this range)
                </Label>
                <p className="mt-0.5 text-[11px] text-sky-800">
                  If your calendar Excel contains more days than this single
                  voyage, set the start date and length below and only the
                  matching days will be imported. Auto-filled from the first
                  parsed row — adjust as needed. Missing dates inside the
                  window become Sea-day placeholders.
                </p>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      type="date"
                      value={uploadStartDate}
                      onChange={(e) => setUploadStartDate(e.target.value)}
                      data-testid="wizard-window-start-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Total Days</Label>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      value={uploadTotalDays || ""}
                      onChange={(e) =>
                        setUploadTotalDays(parseInt(e.target.value, 10) || 0)
                      }
                      placeholder="e.g. 10"
                      data-testid="wizard-window-total-days"
                    />
                  </div>
                </div>
              </div>

              {/* Import button */}
              <Button onClick={handleImport} disabled={importing || dateCol == null} data-testid="wizard-import-btn">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Import dates
              </Button>

              {/* Import result preview */}
              {importResult && (
                <div className="space-y-2 rounded-lg border p-4 bg-muted/30" data-testid="import-result">
                  <p className="text-sm font-medium">{importResult.days?.length} days imported</p>
                  {importResult.suggested_voyage && (
                    <p className="text-xs text-muted-foreground">
                      Suggested: {importResult.suggested_voyage.start_date} · {importResult.suggested_voyage.total_days} days
                    </p>
                  )}
                  <div className="max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Port</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResult.days?.slice(0, 15).map((d, i) => (
                          <TableRow key={i}>
                            <TableCell>{d.day_number}</TableCell>
                            <TableCell>{d.date}</TableCell>
                            <TableCell>{d.port_or_sea}</TableCell>
                            <TableCell>{d.port_name || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Nav */}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={handleToStep3} disabled={!canProceedStep2}>
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 2: Manual ─────────────────────────────────── */}
      {step === 2 && method === "manual" && (
        <div className="space-y-6" data-testid="wizard-step-2-manual">
          <div className="grid gap-4 sm:grid-cols-2 max-w-md">
            <div className="space-y-1.5">
              <Label htmlFor="manual-start">Start Date</Label>
              <Input id="manual-start" type="date" value={manualStartDate} onChange={(e) => setManualStartDate(e.target.value)} data-testid="wizard-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-days">Total Days</Label>
              <Input id="manual-days" type="number" min={1} max={30} value={manualTotalDays} onChange={(e) => setManualTotalDays(parseInt(e.target.value, 10) || 1)} data-testid="wizard-total-days" />
            </div>
          </div>

          {/* Preview */}
          {manualStartDate && manualTotalDays >= 1 && (
            <div className="space-y-1.5">
              <Label>Preview</Label>
              <div className="overflow-x-auto rounded-lg border max-h-[250px] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {generateDefaultDays(manualStartDate, manualTotalDays).map((d) => (
                      <TableRow key={d.day_number}>
                        <TableCell>{d.day_number}</TableCell>
                        <TableCell>{formatDate(d.date)}</TableCell>
                        <TableCell className="text-muted-foreground">Sea</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={handleToStep3} disabled={!canProceedStep2}>
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 3: Review & Create ────────────────────────── */}
      {step === 3 && (
        <div className="space-y-6" data-testid="wizard-step-3">
          {/* Header fields */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="wiz-vn">Voyage Number *</Label>
              <Input id="wiz-vn" value={voyageNumber} onChange={(e) => setVoyageNumber(e.target.value)} required placeholder="e.g. CE-2026-001" data-testid="wizard-voyage-number" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wiz-ship">Ship</Label>
              <Input
                id="wiz-ship"
                value={shipName}
                onChange={(e) => setShipName(e.target.value)}
                placeholder="Ship name"
                data-testid="wizard-ship-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wiz-sd">Start Date</Label>
              <Input id="wiz-sd" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="wizard-start-date-step3" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wiz-td">Total Days</Label>
              <Input id="wiz-td" type="number" min={1} max={30} value={totalDays} onChange={(e) => setTotalDays(parseInt(e.target.value, 10) || 1)} data-testid="wizard-total-days-step3" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wiz-gc">Guest Count</Label>
              <Input id="wiz-gc" type="number" min={0} value={guestCount} onChange={(e) => setGuestCount(parseInt(e.target.value, 10) || 0)} data-testid="wizard-guest-count" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wiz-kc">Kids Count</Label>
              <Input id="wiz-kc" type="number" min={0} value={kidsCount} onChange={(e) => setKidsCount(parseInt(e.target.value, 10) || 0)} data-testid="wizard-kids-count" />
            </div>
          </div>

          {/* Days editor */}
          <div className="space-y-1.5">
            <Label>Days Itinerary ({days.length} days)</Label>
            <DaysTable days={days} onChange={setDays} compact />
          </div>

          {/* Error */}
          {submitError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive" data-testid="wizard-submit-error">
              {submitError}
            </div>
          )}

          {/* Nav */}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button onClick={handleCreate} disabled={creating || !voyageNumber} data-testid="wizard-submit-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Create Voyage
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
